if (!customElements.get("service-variant-select")) {
  customElements.define(
    "service-variant-select",
    class ServiceVariantSelect extends HTMLElement {
      constructor() {
        super();

        this.select = this.querySelector("select");
        this.radios = this.querySelectorAll(".service-variant-select__radio");
        this.selectedValue = this.querySelector(".service-variant-select__selected-value");
        this.details = this.querySelector("details.service-option");
        this.sectionId = this.dataset.sectionId;
        this.productTitle = this.dataset.productTitle || "";
        this.productHandle = this.dataset.productHandle || "";
      }

      connectedCallback() {
        if (!this.select) return;

        this.select.addEventListener("change", this.onVariantChange.bind(this));

        this.radios.forEach((radio) => {
          radio.addEventListener("change", () => {
            if (!radio.checked) return;

            this.select.value = radio.value;
            this.select.dispatchEvent(new Event("change", { bubbles: true }));

            if (this.details) {
              this.details.removeAttribute("open");
            }
          });
        });

        this.onVariantChange();
      }

      onVariantChange() {
        const selectedOption = this.select.options[this.select.selectedIndex];

        if (!selectedOption) return;

        const variantId = selectedOption.value;
        const price = selectedOption.dataset.price || "";
        const originalPrice = selectedOption.dataset.originalPrice || price;
        const wpdPriceCents = selectedOption.dataset.wpdPriceCents || "";
        const hasComparePrice = selectedOption.dataset.hasComparePrice === "true";
        const wpdCompareAtPriceCents = selectedOption.dataset.wpdCompareAtPriceCents || "";
        const sku = selectedOption.dataset.sku || "";
        const title = selectedOption.dataset.title || selectedOption.textContent.trim();
        const isAvailable = true;

        this.syncRadio(variantId);
        this.updateSelectedText(title);
        this.updateHeaderTitle(title);
        this.updateVariantInput(variantId);
        this.updatePrice(price, {
          variantId,
          originalPrice,
          hasComparePrice,
          wpdPriceCents,
          wpdCompareAtPriceCents
        });
        this.reprocessB2BPrices(variantId);
        this.updateSku(sku);
        this.updateAddButton(isAvailable);
        this.updateBrowserUrl(variantId);
      }

      getProductScope() {
        return this.closest("product-info") || document;
      }

      syncRadio(variantId) {
        this.radios.forEach((radio) => {
          radio.checked = radio.value === variantId;
        });
      }

      updateSelectedText(title) {
        if (!this.selectedValue) return;
        this.selectedValue.textContent = title;
      }

      updateVariantInput(variantId) {
        const scope = this.getProductScope();

        const selectors = [
          `#product-form-${this.sectionId} input[name="id"]`,
          `#product-form-installment-${this.sectionId} input[name="id"]`,
          `form[action*="/cart/add"] input[name="id"]`,
          `.product-form input[name="id"]`,
          `.product-variant-id`
        ];

        const inputs = scope.querySelectorAll(selectors.join(", "));

        inputs.forEach((input) => {
          input.value = variantId;
          input.disabled = false;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updatePrice(price, meta = {}) {
        if (!price) return;

        const scope = this.getProductScope();

        const priceSelectors = [
          ".product-head__price .price",
          ".product-price-mobile",
          "[data-service-price]"
        ];

        const priceElements = scope.querySelectorAll(priceSelectors.join(", "));

        priceElements.forEach((priceElement) => {
          priceElement.textContent = price;

          priceElement.classList.remove("wpd-processed");

          if (meta.variantId) {
            priceElement.setAttribute("data-wpd-variant-id", meta.variantId);
          }

          if (meta.wpdPriceCents) {
            priceElement.setAttribute("data-wpd-variant-price", meta.wpdPriceCents);
          }

          if (meta.wpdCompareAtPriceCents) {
            priceElement.setAttribute("data-wpd-variant-compare-at-price", meta.wpdCompareAtPriceCents);
          } else {
            priceElement.setAttribute("data-wpd-variant-compare-at-price", "");
          }

          if (this.productHandle) {
            priceElement.setAttribute("data-wpd-product-handle", this.productHandle);
          }

          const priceWrapper = priceElement.closest(".product-head__price");

          if (priceWrapper) {
            priceWrapper
            .querySelectorAll(".compare-price:not(.service-compare-price)")
            .forEach((staticCompareElement) => {
              staticCompareElement.classList.add("hidden");
              staticCompareElement.style.display = "none";
            });
            let compareElement = priceWrapper.querySelector(".service-compare-price");

            if (!compareElement) {
              compareElement = document.createElement("span");
              compareElement.className = "compare-price service-compare-price hidden";
              priceElement.insertAdjacentElement("beforebegin", compareElement);
            }

            compareElement.textContent = meta.originalPrice || price;
            compareElement.dataset.originalPrice = meta.originalPrice || price;
            compareElement.classList.add("hidden");
          }
        });

        this.syncComparePriceAfterB2B(meta.originalPrice || price, meta.hasComparePrice);
        window.setTimeout(() => this.syncComparePriceAfterB2B(meta.originalPrice || price, meta.hasComparePrice), 600);
        window.setTimeout(() => this.syncComparePriceAfterB2B(meta.originalPrice || price, meta.hasComparePrice), 1400);
      }

      updateSku(sku) {
        const scope = this.getProductScope();
        const skuElement = scope.querySelector(".product__sku");

        if (!skuElement) return;

        if (!sku) {
          skuElement.classList.add("visibility-hidden");
          skuElement.innerHTML = `<span class="visually-hidden">SKU:</span>`;
          return;
        }

        skuElement.classList.remove("visibility-hidden");
        skuElement.innerHTML = `<span class="visually-hidden">SKU:</span>${sku}`;
      }

      updateAddButton(isAvailable) {
        const scope = this.getProductScope();

        const forms = scope.querySelectorAll(".product-form, product-form");

        forms.forEach((formWrapper) => {
          const submitButton =
            formWrapper.querySelector('[name="add"]') ||
            formWrapper.querySelector(".product-form__submit") ||
            formWrapper.querySelector('button[type="submit"]');

          if (!submitButton) return;

          const submitButtonText =
            submitButton.querySelector("span:first-child") ||
            submitButton.querySelector("span") ||
            submitButton;

          if (isAvailable) {
            submitButton.removeAttribute("disabled");
            submitButton.removeAttribute("aria-disabled");

            if (submitButtonText) {
              submitButtonText.textContent = "Request Service";
            }

            return;
          }

          submitButton.setAttribute("disabled", "disabled");
          submitButton.setAttribute("aria-disabled", "true");

          if (submitButtonText) {
            submitButtonText.textContent = "Request Service";
          }
        });
      }

      updateAvailability(isAvailable) {
        const scope = this.getProductScope();

        const availabilityElements = scope.querySelectorAll(".product-availability");

        availabilityElements.forEach((availabilityElement) => {
          availabilityElement.classList.toggle("product__in-stock", isAvailable);
          availabilityElement.classList.toggle("product__sold-out", !isAvailable);
        });
      }

      updateBrowserUrl(variantId) {
        if (!variantId) return;

        const url = new URL(window.location.href);
        url.searchParams.set("variant", variantId);

        window.history.replaceState({}, "", url.toString());
      }

      updateHeaderTitle(variantTitle) {
        const finalTitle = this.buildVariantTitle(variantTitle);

        document.querySelectorAll(".product-head__title-keys").forEach((titleElement) => {
          titleElement.textContent = finalTitle;
        });
      }

      buildVariantTitle(variantTitle) {
        const cleanProductTitle = (this.productTitle || "").trim();
        const cleanVariantTitle = (variantTitle || "").trim();

        if (!cleanVariantTitle || cleanVariantTitle.toLowerCase() === "default title") {
          return cleanProductTitle;
        }

        if (cleanVariantTitle.toLowerCase().includes(cleanProductTitle.toLowerCase())) {
          return cleanVariantTitle;
        }

        return `${cleanProductTitle} ${cleanVariantTitle}`;
      }

      syncComparePriceAfterB2B(originalPrice, hasComparePrice) {
        const normalize = (value) =>
          String(value || "")
            .replace(/\s/g, "")
            .replace(/,/g, "")
            .trim();

        document.querySelectorAll(".product-head__price").forEach((priceWrapper) => {
          const priceElement = priceWrapper.querySelector(".price");
          const compareElement = priceWrapper.querySelector(".service-compare-price");

          if (!priceElement || !compareElement) return;

          if (!hasComparePrice) {
            compareElement.classList.add("hidden");
            compareElement.style.display = "none";
            return;
          }

          const currentPrice = normalize(priceElement.textContent);
          const original = normalize(originalPrice);

          compareElement.textContent = originalPrice;
          compareElement.style.removeProperty("display");

          if (currentPrice && original && currentPrice !== original) {
            compareElement.classList.remove("hidden");
          } else {
            compareElement.classList.add("hidden");
          }
        });
      }

      reprocessB2BPrices(variantId) {
        document.dispatchEvent(
          new CustomEvent("service:variant-change", {
            detail: { variantId }
          })
        );

        [
          "wpdProcessProduct",
          "wpdProcessProducts",
          "wpdProcessPage",
          "wpdProductPageInit"
        ].forEach((functionName) => {
          if (typeof window[functionName] === "function") {
            try {
              window[functionName]();
            } catch (error) {
              console.warn(`B2B price refresh failed on ${functionName}`, error);
            }
          }
        });
      }
    }
  );
}